import React, { useEffect, useState } from 'react'
import { Button, Input, Modal, Select, Table, Tag, Typography } from 'antd'
import StatusBadge from './StatusBadge'
import { FilterFilled, SearchOutlined } from '@ant-design/icons'
import type { ColumnsType, TableRowSelection } from 'antd/es/table/interface'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { accessControlApi, type UserDto } from '../api/access-control.client'
import { useUsersQuery } from '../hooks/useUsersQuery'
import { useMyClientsQuery } from 'enrollment-platform/api/persons'
import { roleUsersQueryKey } from '../hooks/useRoleUsersQuery'
import { ROLES_QUERY_KEY } from '../hooks/useRolesQuery'
import { MY_ABILITY_QUERY_KEY } from '../../../rbac/useMyAbilityQuery'
import shared from './AccessControl.styles.module.scss'
import styles from './AssignUsersModal.module.scss'

function displayName(u: UserDto): string {
  const fullName = [u.firstName, u.lastName].filter(Boolean).join(' ')
  return fullName || u.email || '—'
}

const { Text } = Typography

const PAGE_SIZE = 8

interface Props {
  roleId: string
  open: boolean
  onClose: () => void
  alreadyAssignedIds: string[]
}

export default function AssignUsersModal({
  roleId,
  open,
  onClose,
  alreadyAssignedIds,
}: Props) {
  const queryClient = useQueryClient()
  const [clientPublicKey, setClientPublicKey] = useState<string | undefined>()
  const [searchInput, setSearchInput] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [page, setPage] = useState(1)
  const [selectedIds, setSelectedIds] = useState<string[]>([])

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput)
      setPage(1)
    }, 400)
    return () => clearTimeout(timer)
  }, [searchInput])

  const { data: clients = [], isLoading: clientsLoading } = useMyClientsQuery()

  const { data: pageData, isLoading } = useUsersQuery({
    clientPublicKey,
    search: debouncedSearch || undefined,
    page,
    limit: PAGE_SIZE,
  })
  const users = pageData?.data ?? []
  const total = pageData?.total ?? 0
  const rangeStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1
  const rangeEnd = Math.min(page * PAGE_SIZE, total)

  const { mutate: assign, isPending } = useMutation({
    mutationFn: async () => {
      const merged = Array.from(
        new Set([...alreadyAssignedIds, ...selectedIds])
      )
      await accessControlApi.syncRoleUsers(roleId, merged)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: roleUsersQueryKey(roleId) })
      queryClient.invalidateQueries({ queryKey: ROLES_QUERY_KEY })
      // A newly-assigned user may be the current user — bust their ability cache
      // so menus/route gates reflect the granted access now rather than after
      // the ~5-min staleTime.
      queryClient.invalidateQueries({ queryKey: MY_ABILITY_QUERY_KEY })
      setSelectedIds([])
      setSearchInput('')
      setDebouncedSearch('')
      setClientPublicKey(undefined)
      setPage(1)
      onClose()
    },
  })

  function handleClose() {
    setSelectedIds([])
    setSearchInput('')
    setDebouncedSearch('')
    setClientPublicKey(undefined)
    setPage(1)
    onClose()
  }

  function handleCompanyChange(value: string | undefined) {
    setClientPublicKey(value)
    setPage(1)
  }

  function handleSearchChange(value: string) {
    setSearchInput(value)
  }

  const rowSelection: TableRowSelection<UserDto> = {
    selectedRowKeys: selectedIds,
    onChange: (keys) => setSelectedIds(keys as string[]),
    getCheckboxProps: (record) => ({
      disabled: alreadyAssignedIds.includes(record.cognitoUserId),
    }),
    preserveSelectedRowKeys: true,
  }

  const columns: ColumnsType<UserDto> = [
    {
      title: 'User',
      key: 'user',
      render: (_, u) => (
        <div className={styles.userCell}>
          <Text className={styles.userName}>{displayName(u)}</Text>
          {u.email && <Text className={styles.userEmail}>{u.email}</Text>}
        </div>
      ),
    },
    {
      title: 'Company',
      key: 'company',
      width: 160,
      render: (_, u) => (
        <Text className={styles.companyName}>{u.companyName ?? '—'}</Text>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 90,
      render: (_, u) => <StatusBadge status={u.status} />,
    },
    {
      title: '',
      key: 'assigned',
      width: 90,
      render: (_, u) =>
        alreadyAssignedIds.includes(u.cognitoUserId) ? (
          <Tag color="purple" className={styles.assignedTag}>
            Assigned
          </Tag>
        ) : null,
    },
  ]

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      title="Assign Users to Role"
      width={800}
      footer={
        <div className={styles.footer}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            type="primary"
            loading={isPending}
            disabled={selectedIds.length === 0}
            onClick={() => assign()}
          >
            Assign {selectedIds.length > 0 ? `(${selectedIds.length})` : ''}
          </Button>
        </div>
      }
      styles={{ body: { paddingTop: 16 } }}
    >
      <div className={shared.filterBar}>
        <span className={shared.filterLabel}>
          <FilterFilled />
          Filter by:
        </span>
        <Select
          showSearch
          placeholder="All companies"
          value={clientPublicKey}
          onChange={(v) => handleCompanyChange(v)}
          allowClear
          onClear={() => handleCompanyChange(undefined)}
          loading={clientsLoading}
          disabled={clientsLoading}
          optionFilterProp="children"
          filterOption={(input, option) =>
            String(option?.children ?? '')
              .toLowerCase()
              .includes(input.toLowerCase())
          }
          style={{ minWidth: 200 }}
        >
          {clients.map((c) => (
            <Select.Option key={c.id} value={c.id}>
              {c.name}
            </Select.Option>
          ))}
        </Select>
        <Input
          placeholder="Search by name or email…"
          prefix={<SearchOutlined style={{ color: '#9ca3af' }} />}
          value={searchInput}
          onChange={(e) => handleSearchChange(e.target.value)}
          style={{ flex: 1 }}
          allowClear
          onClear={() => handleSearchChange('')}
        />
      </div>

      <Text type="secondary" className={styles.countText}>
        {selectedIds.length > 0
          ? `${selectedIds.length} user${selectedIds.length > 1 ? 's' : ''} selected`
          : `Showing ${rangeStart}–${rangeEnd} of ${total} user${total !== 1 ? 's' : ''}`}
      </Text>

      <Table
        rowKey="cognitoUserId"
        loading={isLoading}
        dataSource={users}
        columns={columns}
        rowSelection={rowSelection}
        scroll={{ y: 320 }}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total,
          onChange: setPage,
          showSizeChanger: false,
          size: 'small',
        }}
        size="small"
        className={styles.table}
      />
    </Modal>
  )
}
